import { useMemo } from "react";
import { DateTime } from "luxon";
import { isMonthOutOfRange } from "@/lib/utils/isMonthOutOfRange";

/*
 * For a [minYear, maxYear] window, decides whether stepping one calendar month backward or
 * forward should be blocked. When `disabled` is true, both directions are blocked (callers can
 * merge product rules into that flag). Otherwise the reference month is `selectedDate` or today;
 * each direction is blocked if that step would fall outside the window.
 */
interface UseAdjacentMonthDisabledParams {
  minYear: number;
  maxYear: number;
  selectedDate: Date | undefined;
  disabled?: boolean;
}

interface UseAdjacentMonthDisabledResult {
  disablePreviousMonth: boolean;
  disableNextMonth: boolean;
}

export function useAdjacentMonthDisabled({
  minYear,
  maxYear,
  selectedDate,
  disabled,
}: UseAdjacentMonthDisabledParams): UseAdjacentMonthDisabledResult {
  return useMemo(() => {
    const minDate = new Date(minYear, 0, 1);
    const maxDate = new Date(maxYear, 11, 31);

    if (disabled) {
      return { disablePreviousMonth: true, disableNextMonth: true };
    }

    const base = selectedDate
      ? DateTime.fromJSDate(new Date(selectedDate))
      : DateTime.now();
    const prevTarget = base.minus({ months: 1 }).toJSDate();
    const nextTarget = base.plus({ months: 1 }).toJSDate();
    return {
      disablePreviousMonth: isMonthOutOfRange(prevTarget, minDate, maxDate),
      disableNextMonth: isMonthOutOfRange(nextTarget, minDate, maxDate),
    };
  }, [selectedDate, disabled, minYear, maxYear]);
}
